import type { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import { DefaultJobAd, JobAd } from '../Models/JobAd';
import { DefaultJobDetails, JobDetails } from '../Models/JobDetails';
import { OneClickRecruitmentPrefillResponse } from './api/useOneClickCreateRecruitment';
import { CreateRecruitmentContextState } from './Contexts/CreateRecruitmentContextState';
import { JobAdSkillsDTO } from '@/DTO/Assessments/JobAdSkillsDTO';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import { Currency } from '@/Models/Currency';
import { ExperienceCriteria } from '@/Models/ExperienceCriteria';
import JobAdSkillsType from '@/Models/JobAdSkillsType';

/**
 * The slice of {@link CreateRecruitmentContextState} that a one-click fallback pre-fills.
 */
export type OneClickPrefillState = Pick<CreateRecruitmentContextState, 'jobDetails' | 'jobAd' | 'experienceCriteria'>;

function mapSkills(skills: JobAdSkillsDTO[], type: JobAdSkillsType): AutocompleteOption[] {
    return skills
        .filter((skill) => skill.skillType === type)
        .map((skill) => {
            return { label: skill.skillName }; // all prefill skills are user defined which means they do not have the id.
        });
}

/**
 * Maps a one-click fallback prefill payload onto the manual-wizard context state.
 */
export function mapOneClickPrefillToState(
    client: ClientResponse,
    prefill: OneClickRecruitmentPrefillResponse,
    currencies: Currency[],
): OneClickPrefillState {
    const isSalarySet = prefill.jobDetails.currency !== null && prefill.jobDetails.currency !== '';

    const jobDetails: JobDetails = {
        name: prefill.jobDetails.name ?? DefaultJobDetails.name,
        workArrangement: prefill.jobDetails.workArrangement ?? DefaultJobDetails.workArrangement,
        locationDetails: prefill.jobDetails.locationDetails ?? DefaultJobDetails.locationDetails,
        jobFunctionId: prefill.jobDetails.jobFunctionId ?? DefaultJobDetails.jobFunctionId,
        workExperience: prefill.jobDetails.workExperience ?? DefaultJobDetails.workExperience,
        employmentType: prefill.jobDetails.employmentType ?? DefaultJobDetails.employmentType,
        directReports: prefill.jobDetails.directReports ?? DefaultJobDetails.directReports,
        yearlySalaryFrom: isSalarySet
            ? String(prefill.jobDetails.yearlySalaryFrom!)
            : DefaultJobDetails.yearlySalaryFrom,
        yearlySalaryTo: isSalarySet ? String(prefill.jobDetails.yearlySalaryTo!) : DefaultJobDetails.yearlySalaryTo,
        currency: isSalarySet
            ? (currencies.find((x) => x.id === prefill.jobDetails.currency) ?? DefaultJobDetails.currency)
            : DefaultJobDetails.currency,
        doNotDisplaySalary: isSalarySet ? false : DefaultJobDetails.doNotDisplaySalary,
        jobDescriptionLanguage: DefaultJobDetails.jobDescriptionLanguage,
        jobPostingType: client.isDefault ? 'Direct' : 'OnBehalf',
    };

    const skills = prefill.jobAd.skills ?? [];
    const hardSkills = mapSkills(skills, JobAdSkillsType.HardSkill);
    const softSkills = mapSkills(skills, JobAdSkillsType.SoftSkill);

    const jobAd: JobAd = {
        title: prefill.jobAd.title ?? DefaultJobAd.title,
        description: prefill.jobAd.description ?? DefaultJobAd.description,
        descriptionLen: prefill.jobAd.description ? prefill.jobAd.description.length : DefaultJobAd.descriptionLen,
        companyDescription: prefill.jobAd.companyDescription ?? DefaultJobAd.companyDescription,
        hardSkills,
        hardSkillsSwitch: hardSkills.length > 0,
        softSkills,
        softSkillsSwitch: softSkills.length > 0,
        status: DefaultJobAd.status,
    };

    const experienceCriteria: ExperienceCriteria[] = (prefill.experienceCriteria ?? []).map(({ criterion }) => ({
        id: '',
        criterion,
    }));

    return {
        jobDetails,
        jobAd,
        experienceCriteria,
    };
}
